// components/PriceVolumeAlertsManager.js
import React, { useEffect, useState } from 'react';
import './css/PriceVolumeAlertsManager.css';
import PriceVolumeAlertPopup from '@dashboard/pricevolumealerts/PriceVolumeAlertPopup';
import { tickStorage } from '@dashboard/contexts/tickStore';
import { apiFetch as fetch } from 'apiClient';
import dayjs from 'dayjs';
import {useStackedPopup} from "@dashboard/contexts/PopupStackContext";
import Draggable from 'react-draggable';
import {useGlobalEvents} from "@dashboard/contexts/GlobalEventsContext";

const statusLabel = (alert) => {
    if (alert.cleared) return 'Cleared';
    if (alert.triggered) return 'Triggered';
    return 'Active';
};

const buildConditionSummary = (alert) => {
    const parts = [];

    if (alert.priceValue != null && alert.priceDirection) {
        parts.push(`Price ${alert.priceDirection} ${alert.priceValue}`);
    }
    if (alert.volumeValue != null && alert.volumeDirection) {
        parts.push(`Volume ${alert.volumeDirection} ${alert.volumeValue}`);
    }
    if (alert.depthValue != null && alert.depthDirection && alert.depthSide) {
        parts.push(
            `Top ${alert.depthSide} qty ${alert.depthDirection} ${alert.depthValue}`
        );
    }

    return parts.length ? parts.join(' · ') : '-';
};

const formatDateTime = (value) => {
    if (!value) return '-';
    return dayjs(value).format('YYYY-MM-DD HH:mm');
};

const formatValidity = (alert) => {
    if (!alert.validityType) return '-';
    if (alert.validityType === 'TILL_DATE') {
        return alert.validTillDate
            ? `Till ${dayjs(alert.validTillDate).format('YYYY-MM-DD')}`
            : 'Till date';
    }
    if (alert.validityType === 'TODAY') return 'Today';
    if (alert.validityType === 'FOREVER') return 'Forever';
    return alert.validityType;
};

const PriceVolumeAlertsManager = ({ open, onClose }) => {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [editingAlert, setEditingAlert] = useState(null);
    const [popupOpen, setPopupOpen] = useState(false);
    const [showCleared, setShowCleared] = useState(false);
    const {
        highlightedPriceVolumeAlertId,
        clearHighlightedPriceVolumeAlert,
        clearPriceVolumeAlert,
    } = useGlobalEvents();

    useStackedPopup(open, () => onClose());

    useEffect(() => {
        if (!open) return;
        fetchAlerts();
    }, [open, showCleared]);  // ← include showCleared here


    const fetchAlerts = async () => {
        setLoading(true);
        setError(null);

        try {
            const res = await fetch(`/api/price-volume-alerts${showCleared ? '' : '?onlyActive=true'}`);
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || `Failed with status ${res.status}`);
            }
            const data = await res.json();
            const list = Array.isArray(data) ? data : [];

            // sort newest first by createdAt if present
            list.sort((a, b) => {
                const ca = a.createdAt || '';
                const cb = b.createdAt || '';
                return cb.localeCompare(ca);
            });

            setAlerts(list);
        } catch (e) {
            console.error('Failed to fetch alerts', e);
            setError(e.message || 'Failed to fetch alerts.');
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (alert) => {
        setEditingAlert(alert);
        setPopupOpen(true);
    };

    const handleClearClick = async (alert) => {
        try {
            const res = await fetch(`/api/price-volume-alerts/${alert.id}/clear`, {
                method: 'POST'
            });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || `Failed with status ${res.status}`);
            }

            // Update status locally
            setAlerts((prev) =>
                prev.map((a) =>
                    a.id === alert.id
                        ? { ...a, cleared: true, clearedAt: new Date().toISOString() }
                        : a
                )
            );

            // Also update global context (header badge)
            clearPriceVolumeAlert(alert.id);
            if (highlightedPriceVolumeAlertId === alert.id) {
                clearHighlightedPriceVolumeAlert();
            }
        } catch (e) {
            console.error('Failed to clear alert', e);
            setError(e.message || 'Failed to clear alert.');
        }
    };

    const handleDeleteClick = async (alert) => {
        if (!window.confirm('Delete this alert permanently?')) return;

        try {
            const res = await fetch(`/api/price-volume-alerts/${alert.id}`, {
                method: 'DELETE'
            });

            if (!res.ok) throw new Error("Failed deleting alert");

            setAlerts(prev => prev.filter(a => a.id !== alert.id));
            clearPriceVolumeAlert(alert.id);
            if (highlightedPriceVolumeAlertId === alert.id) {
                clearHighlightedPriceVolumeAlert();
            }
        } catch (e) {
            console.error(e);
            setError(e.message);
        }
    };

    const handlePopupClose = () => {
        setPopupOpen(false);
        setEditingAlert(null);
    };

    if (!open) {
        return null;
    }


    const handleAlertSaved = (result) => {
        // Case 1: popup reports a deletion
        if (result && result.deleted) {
            setAlerts(prev => prev.filter(a => a.id !== result.deleted));
            handlePopupClose();
            return;
        }

        // Case 2: normal create/update
        setAlerts((prev) => {
            const withoutOld = editingAlert
                ? prev.filter((a) => a.id !== editingAlert.id)
                : prev.filter((a) => a.id !== result.id);

            return [result, ...withoutOld];
        });
        handlePopupClose();
    };


    if (!open) {
        return null;
    }

    return (
        <div
            className="pva-manager-backdrop"
            onClick={(e) => {
                // prevent accidental backdrop close
                e.stopPropagation();
            }}
        >
            <Draggable handle=".pva-manager-header">
            <div
                className="pva-manager-container"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="pva-manager-header">
                    <h2>Manage Price / Volume Alerts</h2>
                    <div className="pva-manager-header-right">
                        <label style={{ display: 'flex', alignItems: 'center', gap:'5px', color:'white', cursor:'pointer', fontSize:'12px' }}>
                            <input
                                type="checkbox"
                                checked={showCleared}
                                onChange={() => {
                                    setAlerts([]);
                                    setShowCleared(prev => !prev);
                                }}
                                style={{ cursor:'pointer' }}
                            />
                            Show cleared
                        </label>

                        <button
                            className="pva-manager-refresh-btn"
                            onClick={fetchAlerts}
                        >
                            Refresh
                        </button>
                        <button
                            className="pva-manager-close-btn"
                            onClick={onClose}
                        >
                            ✕
                        </button>
                    </div>
                </div>

                <div className="pva-manager-body">
                    {loading && (
                        <div className="pva-manager-status">Loading alerts…</div>
                    )}
                    {error && (
                        <div className="pva-manager-error">
                            {error}
                        </div>
                    )}

                    {!loading && !error && alerts.length === 0 && (
                        <div className="pva-manager-status">
                            No alerts found in database.
                        </div>
                    )}

                    {!loading && !error && alerts.length > 0 && (
                        <table className="pva-manager-table">
                            <thead>
                            <tr>
                                <th>Instrument</th>
                                <th>Conditions</th>
                                <th>Validity</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Triggered</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {alerts.map((a) => {
                                const baseClass =
                                    a.triggered && !a.cleared
                                        ? 'pva-row-triggered'
                                        : a.cleared
                                            ? 'pva-row-cleared'
                                            : '';

                                const highlightClass =
                                    highlightedPriceVolumeAlertId === a.id
                                        ? ' pva-row-highlight'
                                        : '';

                                return (
                                    <tr
                                        key={a.id}
                                        className={baseClass + highlightClass}
                                    >
                                        <td>
                                            <div className="pva-instrument-main">
                                                {a.tradingsymbol} ({a.exchange})
                                            </div>
                                        </td>
                                        <td>{buildConditionSummary(a)}</td>
                                        <td>{formatValidity(a)}</td>
                                        <td>{statusLabel(a)}</td>
                                        <td>{formatDateTime(a.createdAt)}</td>
                                        <td>{formatDateTime(a.triggeredAt)}</td>
                                        <td>
                                            <button className="pva-action-btn"
                                                    onClick={() => handleEditClick(a)}>
                                                Edit
                                            </button>

                                            {!a.cleared ? (
                                                <button
                                                    className="pva-action-btn pva-action-btn--secondary"
                                                    onClick={() => handleClearClick(a)}>
                                                    Clear
                                                </button>
                                            ) : (
                                                <button
                                                    className="pva-action-btn pva-action-btn--secondary"
                                                    style={{ background:'#ef5350', color:'white' }}
                                                    onClick={() => handleDeleteClick(a)}>
                                                    Delete
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>

                        </table>
                    )}
                </div>

                {popupOpen && editingAlert && (
                    <PriceVolumeAlertPopup
                        open={popupOpen}
                        onClose={handlePopupClose}
                        instrument={{
                            instrumentToken: editingAlert.instrumentToken,
                            tradingsymbol: editingAlert.tradingsymbol,
                            exchange: editingAlert.exchange
                        }}
                        tick={tickStorage.latest[editingAlert.instrumentToken] || null}
                        mode="edit"
                        alert={editingAlert}
                        onSaved={handleAlertSaved}
                    />
                )}
            </div>
            </Draggable>

        </div>
    );
};

export default PriceVolumeAlertsManager;
